import { BaseBuildTableData } from '../FilterResultsManager/types';
import { buildGnomadUrlFromVariant, sangerUrlFromVariant } from '../tableUtils';
import { ExternalLink } from '@lifeomic/chromicons';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { Link } from '@lifeomic/chroma-react/components/Link';
import { messages } from './intlLabels';
import { sharedMessages } from '../sharedIntlLabels';
import { SpecificityIndicator } from '../Indicators';
import { TableConfiguration } from '@lifeomic/chroma-react/components/TableModule';
import * as React from 'react';
import urls from '../../../../src/util/urls';
import {
  getAAChange,
  getGeneClass,
  getNNChange,
} from '../FilterResultsManager/ShortVariantsContent/buildTableData';
import { getAminoAcidField } from '../utils';

const chr2Num = (chr: any) =>
  parseInt(
    chr
      .replace('chr', '')
      .replace('X', '23')
      .replace('Y', '24')
      .replace('M', '25'),
    10,
  );

const getVariantDetail = ({
  formatMessage,
  value,
  account,
}: {
  formatMessage: any;
  value: any;
  account: string;
}) => {
  if (value.expression) {
    return `${value.expression} ${
      value.expressionUnit && value.expressionUnit.toUpperCase()
    }`;
  } else if (value.copyNumber) {
    return formatMessage(messages.copyNumberValue, {
      copyNumber: value.copyNumber,
    });
  } else if (value.inFrame) {
    return formatMessage(messages.inFrameValue, {
      inFrame: value.inFrame,
    });
  } else if (value.ensemblCanon) {
    const aminoAcidField = getAminoAcidField(account);
    const aaChange = getAAChange(value, aminoAcidField);
    const nnChange = getNNChange(value);

    if (!nnChange) {
      return aaChange;
    }

    if (!aaChange) {
      return nnChange;
    }

    return (
      <>
        {aaChange} | {nnChange}
      </>
    );
  }
};

interface BuildTableData extends BaseBuildTableData {
  account: string;
  activeSequence: {
    genomeBuild: string;
  };
  classes?: Record<string, string>;
  summary?: {
    genomeBuild: string;
  };
}

export const buildTableData = ({
  account,
  activeSequence,
  classes,
  formatMessage,
  onSort,
  summary,
}: BuildTableData): Array<TableConfiguration> => [
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.ckb),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['drugAssociations.byGene'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { drugAssociations } = value;

        if (!drugAssociations) {
          return null;
        }

        const { byGene = [], byGeneAndFeature = [] } = drugAssociations;

        if (value.variantType === 'shortVariant') {
          const gene = value.ensemblCanon && value.ensemblCanon[0].gene;
          const anyGeneMatch = [...byGene, ...byGeneAndFeature].some(
            (association = {}) => association.gene === gene,
          );

          if (!anyGeneMatch) {
            return null;
          }
        }

        return (
          <SpecificityIndicator
            match={byGeneAndFeature.length > 0 ? 'variant' : 'gene'}
          />
        );
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.gene),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: [
            (v: any) =>
              v.gene ||
              v.geneId ||
              v.geneId1 ||
              v?.ensemblCanon?.[0]?.gene?.toLowerCase() ||
              '',
          ] as any,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        if (value.variantType === 'structuralVariant') {
          const geneId1 = value?.geneId1;
          const geneId2 = value?.geneId2;

          if (!geneId1 && !geneId2) {
            return null;
          }

          return (
            <>
              <Link to={urls.ncbi.gene(geneId1)} newTab>
                {geneId1}
              </Link>
              {geneId2 && (
                <>
                  <span>-</span>
                  <Link to={urls.ncbi.gene(geneId2)} newTab>
                    {geneId2}
                  </Link>
                </>
              )}
            </>
          );
        } else {
          const gene =
            value?.ensemblCanon?.[0]?.gene || value.geneId || value.gene;
          if (!gene) {
            return null;
          }

          return (
            <Link to={urls.ncbi.gene(gene)} newTab>
              {gene}
            </Link>
          );
        }
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(messages.variantDetail),
    },
    cell: {
      content: value => getVariantDetail({ formatMessage, value, account }),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.variantClass),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: [
            (v: any) => v.type || v.status || v?.ensemblCanon?.[0]?.class,
          ] as any,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value =>
        value.type || value.status || value?.ensemblCanon?.[0]?.class,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.position),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: [
            (v: any) => chr2Num(v.chromosome || v.chromosome1 || ''),
            (v: any) => v.position || v.startPosition1 || v.startPosition,
          ] as any,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        if (value.variantType === 'shortVariant') {
          const position = value?.position;
          const chromosome = value?.chromosome;
          if (!position) {
            return null;
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          } else if (!chromosome || !summary.genomeBuild) {
            return position;
          }

          return (
            <Link
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              to={urls.ucsc.genome(summary.genomeBuild, chromosome, position)}
              newTab
            >
              {`${chromosome}:${position}`}
            </Link>
          );
        } else if (value.variantType === 'copyNumberVariant') {
          const startPosition = value?.startPosition;
          const endPosition = value?.endPosition;
          const chromosome = value?.chromosome;
          if (!(startPosition && endPosition)) {
            return null;
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          } else if (!summary.genomeBuild && chromosome) {
            return `${chromosome}:${startPosition}-${endPosition}`;
            // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          } else if (!chromosome || !summary.genomeBuild) {
            return `${startPosition}-${endPosition}`;
          }
          return (
            <Link
              to={urls.ucsc.genome(
                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                summary.genomeBuild,
                chromosome,
                `${startPosition}-${endPosition}`,
              )}
              newTab
            >
              {`${chromosome}:${startPosition}-${endPosition}`}
            </Link>
          );
        } else if (value.variantType === 'structuralVariant') {
          const startPosition = value?.startPosition1;
          const endPosition = value?.endPosition1;
          const chromosome = value?.chromosome1;
          const startPosition2 = value?.startPosition2;
          const endPosition2 = value?.endPosition2;
          const chromosome2 = value?.chromosome2;
          const pos1 = `${chromosome ? chromosome + ':' : null}${
            startPosition === endPosition
              ? startPosition
              : startPosition + '-' + endPosition
          }`;
          const pos2 = `${chromosome2 ? chromosome2 + ':' : null}${
            startPosition2 === endPosition2
              ? startPosition2
              : startPosition2 + '-' + endPosition2
          }`;
          if (
            !(
              (startPosition && endPosition) ||
              (startPosition2 && endPosition2)
            )
          )
            return null;
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          if (!summary.genomeBuild || !chromosome) return `${pos1} | ${pos2}`;
          return (
            <span>
              <Link
                to={urls.ucsc.genome(
                  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                  summary.genomeBuild,
                  chromosome,
                  `${startPosition}-${endPosition}`,
                )}
                newTab
              >
                {`${pos1}`}
              </Link>
              &nbsp;|&nbsp;
              <Link
                to={urls.ucsc.genome(
                  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                  summary.genomeBuild,
                  chromosome2,
                  `${startPosition2}-${endPosition2}`,
                )}
                newTab
              >
                {`${pos2}`}
              </Link>
            </span>
          );
        }
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(messages.variantType),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: [(v: any) => v.sequenceType + v.variantType] as any,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: v => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        return formatMessage(messages.sequenceTypeAndVariantType, {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          sequenceType: formatMessage(sharedMessages[v.sequenceType]),
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          variantType: formatMessage(messages[v.variantType]),
        });
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.variantAlleleFreq),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['vcf.variantAllelicFrequency'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.vcf?.variantAllelicFrequency,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.zygosity),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['zygosity'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { zygosity } = value;

        if (!zygosity) {
          return null;
        }

        if (zygosity.toLowerCase() === 'heterozygous') {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          return formatMessage(sharedMessages.heterozygous);
        }

        if (zygosity.toLowerCase() === 'homozygous') {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          return formatMessage(sharedMessages.homozygous);
        }

        if (zygosity.toLowerCase() === 'reference') {
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          return formatMessage(sharedMessages.heterozygousReference);
        }

        return null;
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.genomesFreq),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['gnomadAlleleFrequency'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { gnomadAlleleFrequency } = value;

        if (!gnomadAlleleFrequency) {
          return null;
        }

        return buildGnomadUrlFromVariant({
          variant: value,
          displayValue: gnomadAlleleFrequency,
          referenceSetId: activeSequence.genomeBuild,
        });
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.genomesHom),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['gnomadHomozygous'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value.gnomadHomozygous,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.exomesFreq),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['exacAlleleFrequency'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { exacAlleleFrequency } = value;

        if (!exacAlleleFrequency) {
          return null;
        }

        return buildGnomadUrlFromVariant({
          variant: value,
          displayValue: exacAlleleFrequency,
          referenceSetId: activeSequence.genomeBuild,
        });
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.exomesHom),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['exacHomozygous'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value.exacHomozygous,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.dbsnpRsId),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['rsid'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { rsid } = value;

        if (!rsid) {
          return null;
        }

        return (
          <Link to={urls.ncbi.rsid(rsid)} newTab>
            {rsid}
          </Link>
        );
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.clinVarSignificance),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['clinvar.significance'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { clinvar } = value;
        if (!clinvar) {
          return null;
        }

        const { significance, alleleId } = clinvar;

        if (!significance) {
          return null;
        }

        if (!alleleId) {
          return significance;
        }

        return (
          <Link to={urls.ncbi.clinvar(alleleId)} newTab>
            {significance}
          </Link>
        );
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.clinVarDisease),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['clinvar.disease'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.clinvar?.disease,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.cosmicStatus),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['cosmic.status'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { cosmic } = value;

        if (!cosmic) {
          return null;
        }

        const { status } = value.cosmic;

        if (!status) {
          return null;
        }

        const sangerUrl = sangerUrlFromVariant(value);

        if (!sangerUrl) {
          return status;
        }

        return (
          <Link to={sangerUrl} newTab>
            {status}
          </Link>
        );
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.cosmicCount),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['cosmic.sampleCount'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.cosmic?.sampleCount,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.damagingPercentage),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['dnbsfp.damagingVote'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { damagingCount, damagingPredictor, damagingVote } = value;

        if (!damagingVote) {
          return null;
        }

        return `${Math.round(damagingVote * 100)}% (${damagingCount || 0}/${
          damagingPredictor || 0
        })`;
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.damagingRankScore),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['dnbsfp.meanRankScore'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { dbnsfp } = value;

        if (!dbnsfp) {
          return null;
        }

        const { meanRankScore, meanRankScorePredictor } = dbnsfp;

        if (!meanRankScore) {
          return null;
        }

        return `${meanRankScore || 0.0} (${meanRankScorePredictor || 0})`;
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.sift),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['dnbsfp.siftPred'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { dbnsfp } = value;

        if (!dbnsfp) {
          return null;
        }

        const { siftPred } = dbnsfp;

        if (!siftPred) {
          return null;
        }

        return siftPred.join(',');
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.mutTaster),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['dnbsfp.mutationTasterPred'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { dbnsfp } = value;

        if (!dbnsfp) {
          return null;
        }

        const { mutationTasterPred } = dbnsfp;

        if (!mutationTasterPred) {
          return null;
        }

        return mutationTasterPred.join(',');
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.fathmm),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['dnbsfp.fathmmPred'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const { dbnsfp } = value;

        if (!dbnsfp) {
          return null;
        }

        const { fathmmPred } = dbnsfp;

        if (!fathmmPred) {
          return null;
        }

        return fathmmPred.join(',');
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.geneId),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['ensemblCanon[0].geneId'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const geneId = value?.ensemblCanon?.[0]?.geneId;

        if (!geneId) {
          return null;
        }

        return (
          <Link to={urls.ensemble.gene(geneId)} newTab>
            {geneId}
          </Link>
        );
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.transcriptId),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['ensemblCanon[0].transcriptId'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const transcriptId = value?.ensemblCanon?.[0]?.transcriptId;

        if (!transcriptId) {
          return null;
        }

        return (
          <Link to={urls.ensemble.gene(transcriptId)} newTab>
            {transcriptId}
          </Link>
        );
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.geneClass),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['ensemblCanon[0].biotype'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => getGeneClass(value),
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.ref),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['reference'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value.reference,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.alt),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['alternate'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value.alternate,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.refAltDepth),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['vcf.coverage'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.vcf?.coverage,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.vcfQual),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['vcf.quality'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.vcf?.quality,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.vcfFilter),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['vcf.filter'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.vcf?.filter,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.codingEffect),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['ensemblCanon[0].group'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.ensemblCanon?.[0]?.group,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.exonNumber),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['ensemblCanon[0].exonIntronRank'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.ensemblCanon?.[0]?.exonIntronRank,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.impact),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['ensemblCanon[0].impact'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.ensemblCanon?.[0]?.impact,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.minFreq),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['minimumAlleleFrequency'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value.minimumAlleleFrequency,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.maxFreq),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['maximumAlleleFrequency'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value.maximumAlleleFrequency,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.clinVarId),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['clinvar.alleleId'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const alleleId = value?.clinvar?.alleleId;
        if (!alleleId) {
          return null;
        }

        return (
          <Link to={urls.ncbi.clinvar(alleleId)} newTab>
            {alleleId}
          </Link>
        );
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.clinVarReview),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['clinvar.review'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.clinvar?.review,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.clinVarSubmission),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['clinvar.submission'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.clinvar?.submission,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.clinVarNearby),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['clinvar.nearVariant'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.clinvar?.nearVariant,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.cosmicId),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['cosmic.cosmicId'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => {
        const cosmicId = value?.cosmic?.cosmicId;

        if (!cosmicId) {
          return null;
        }

        const sangerUrl = sangerUrlFromVariant(value);

        if (!sangerUrl) {
          return cosmicId;
        }

        return (
          <Link to={sangerUrl} newTab>
            {cosmicId}
          </Link>
        );
      },
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.cosmicHistology),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['cosmic.histology'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.cosmic?.histology,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.cosmicSite),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['cosmic.tumorSite'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.cosmic?.tumorSite,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.cosmicNearby),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['cosmic.nearVariant'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.cosmic?.nearVariant,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.labReportedSignificance),
      onSort: ({ sortDirection }) => {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onSort({
          keys: ['vcf.vendSig'],
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          directions: sortDirection ? [sortDirection] : null,
        });
      },
    },
    cell: {
      content: value => value?.vcf?.vendSig,
    },
  },
  {
    header: {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      label: formatMessage(sharedMessages.igv),
    },
    cell: {
      content: value => {
        if (value.variantType === 'shortVariant') {
          const { chromosome, position } = value;
          const choromosomeValue = chromosome.replace('chr', '');

          if (!choromosomeValue) {
            return null;
          }

          if (!position && !Number.isInteger(position)) {
            return null;
          }

          const startPos = position - 50;
          const endPos = position + 50;
          const location = `${choromosomeValue}:${startPos}-${endPos}`;
          const url = urls.app.genomeViewer({
            account,
            location,
            genome: activeSequence.genomeBuild,
            variantSet: value.variantSetId,
            readSets: value.readGroups,
          });

          return (
            <IconButtonLink
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              aria-label={formatMessage(sharedMessages.viewIgv)}
              className={classes?.innerIconButton}
              paddingTop={0}
              paddingBottom={0}
              paddingLeft={0}
              paddingRight={0}
              icon={ExternalLink}
              size={0}
              to={url}
              target="_blank"
              rel="noopener noreferrer"
              // Stop propagation so the table-row click handler
              // doesn't trigger
              onClick={e => e.stopPropagation()}
            />
          );
        }
        return null;
      },
    },
  },
];
